// lib/flux-3-klein.ts
import type { Flux3KleinResolution } from '#types/types';

export const FLUX_3_KLEIN_MAX_INPUT_IMAGES = 10;

/** The input budget the kernel renders the source at; Klein's output size is sent explicitly, so it does not follow it. */
export const FLUX_3_KLEIN_MAX_INPUT_PIXELS = 4_000_000;

export const FLUX_3_KLEIN_DEFAULT_RESOLUTION: Flux3KleinResolution = '2k';

// Owner decision, 2026-10-16: the highest margin, so FLUX 3 Image's grid; read by the registry estimator, the only price source.
export const FLUX_3_KLEIN_RESOLUTION_CREDITS: Readonly<Record<Flux3KleinResolution, number>> = {
  '1k': 1,
  '2k': 1.5,
  '4k': 2.5,
};

// Equal-area tiers in pixels; 4k is fal's own ceiling of 4096x4096.
const FLUX_3_KLEIN_TIER_PIXELS: Readonly<Record<Flux3KleinResolution, number>> = {
  '1k': 1024 * 1024,
  '2k': 2048 * 2048,
  '4k': 4096 * 4096,
};

// fal's image_size contract for Klein: each side 256 to 4096, a multiple of 16.
const MIN_SIDE = 256;
const MAX_SIDE = 4096;
const STEP = 16;

/** Only an absent resolution takes the default; anything else must name a tier. */
export const resolveFlux3KleinResolution = (value: unknown): Flux3KleinResolution | null => {
  if (value === undefined) return FLUX_3_KLEIN_DEFAULT_RESOLUTION;
  if (typeof value !== 'string') return null;
  const resolution = value.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(FLUX_3_KLEIN_RESOLUTION_CREDITS, resolution) ? resolution as Flux3KleinResolution : null;
};

/**
 * The output size for a frame of width:height at a tier: the frame's own shape at the tier's pixel area, each side
 * a multiple of 16 inside 256..4096. A frame past the 16:1 the box allows is squeezed to its edge, never refused.
 */
export const deriveFlux3KleinImageSize = (width: unknown, height: unknown, resolution: Flux3KleinResolution): { width: number; height: number } => {
  const valid = typeof width === 'number' && typeof height === 'number' && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
  const ratio = valid ? (width as number) / (height as number) : 1;
  const area = FLUX_3_KLEIN_TIER_PIXELS[resolution];
  let h = Math.sqrt(area / ratio);
  let w = h * ratio;
  const over = Math.max(w / MAX_SIDE, h / MAX_SIDE, 1);
  w /= over;
  h /= over;
  const side = (v: number) => Math.min(MAX_SIDE, Math.max(MIN_SIDE, Math.floor(v / STEP) * STEP));
  return { width: side(w), height: side(h) };
};

export const getFlux3KleinEndpoint = (hasImages: boolean): string => (
  `blackforestlabs/flux-3/klein/${hasImages ? 'edit-image' : 'text-to-image'}`
);

export const buildFlux3KleinProviderInput = (input: {
  prompt: string;
  imageSize: { width: number; height: number };
  imageUrls?: readonly string[];
}) => {
  const imageUrls = (input.imageUrls ?? [])
    .filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index)
    .slice(0, FLUX_3_KLEIN_MAX_INPUT_IMAGES);
  return {
    prompt: input.prompt,
    image_size: input.imageSize,
    num_images: 1,
    // BFL: Klein takes full scene captions, and a request sent as typed gives generic output. The URW benchmark ran with it on.
    enable_prompt_expansion: true,
    // The FLUX Kommunity License requires content filtering on production use.
    enable_safety_checker: true,
    output_format: 'png' as const,
    // A data-URI response would blow up the fal.subscribe payload.
    sync_mode: false,
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};
